import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams, Navigate, Link, useNavigate } from 'react-router-dom';
import { ApolloProvider, ApolloClient, InMemoryCache, HttpLink, from, useQuery } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { CssBaseline, Box, Typography, AppBar, Toolbar, Button, CircularProgress, Chip } from '@mui/material';
import { OfferLatest } from './offer';
import BoLanguageSelector from '../../components/BoLanguageSelector';
import { ME_QUERY_MINIMAL } from '../../layout/login';
import { OFFER_VIEW_QUERY } from './gql';
import { getStoredGuestOfferToken, getStoredGuestOfferId, setStoredGuestOfferAccess } from './guest-offer-storage';
import logo from '../../assets/images/logo.svg';
import i18next from 'i18next';

export { getStoredGuestOfferToken, getStoredGuestOfferId } from './guest-offer-storage';

const httpLinkUrl = process.env.REACT_APP_GRAPHQL_ENDPOINT || '/backend/graphql';

function GuestOfferHeader({ offerId, loginFrom }: { offerId: number; loginFrom: string }) {
  const { data, loading } = useQuery(OFFER_VIEW_QUERY, {
    variables: { id: offerId },
    fetchPolicy: 'cache-first',
  });
  const hotelName = data?.viewWlOffer?.hotel?.name;

  return (
    <AppBar position="static" color="default" elevation={1}>
      <Toolbar sx={{ gap: 2, minHeight: { xs: 56, sm: 64 } }}>
        <Box
          component="img"
          src={logo}
          alt="seminargo"
          sx={{ height: 30, width: 'auto', flexShrink: 0, ml: 0.5 }}
        />
        <Box sx={{ flex: 1 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {loading ? (
            <CircularProgress size={20} />
          ) : (
            hotelName && (
              <Typography variant="subtitle1" fontWeight={600} noWrap sx={{ maxWidth: { xs: 140, sm: 280, md: 400 } }}>
                {hotelName}
              </Typography>
            )
          )}
          <Chip label={i18next.t('offer-guest-user-type')} size="small" color="primary" variant="outlined" />
          <BoLanguageSelector />
          <Button
            component={Link}
            to={{ pathname: '/login', search: `?from=${encodeURIComponent(loginFrom)}` }}
            variant="contained"
            size="small"
          >
            {i18next.t('offer-guest-sign-in')}
          </Button>
        </Box>
      </Toolbar>
    </AppBar>
  );
}

function GuestOfferPageInner({ offerId, probeClient }: { offerId: number; probeClient: ApolloClient<unknown> }) {
  const navigate = useNavigate();
  const [redirecting, setRedirecting] = useState(false);

  // Probe JWT session without offer-token headers (me is not allowed in guest GraphQL mode).
  useEffect(() => {
    let cancelled = false;
    probeClient
      .query({ query: ME_QUERY_MINIMAL, fetchPolicy: 'network-only' })
      .then(meResult => {
        if (cancelled || !meResult.data?.me) return undefined;
        return probeClient.query({ query: OFFER_VIEW_QUERY, variables: { id: offerId }, fetchPolicy: 'network-only' });
      })
      .then(offerResult => {
        if (cancelled || !offerResult?.data?.viewWlOffer) return;
        setRedirecting(true);
        navigate(`/offers/${offerId}`, { replace: true });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [offerId, navigate, probeClient]);

  // Plain path — Link encodes once via search param (SM-2324 post-login return)
  const loginFrom = `/offers/${offerId}`;

  if (redirecting) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <GuestOfferHeader offerId={offerId} loginFrom={loginFrom} />
      <Box sx={{ p: 2 }}>
        <OfferLatest id={offerId} guestMode />
      </Box>
    </Box>
  );
}

export default function GuestOfferPage() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const offerId = parseInt(`${id}`, 10);
  const tokenFromUrl = searchParams.get('token');

  useEffect(() => {
    if (tokenFromUrl && Number.isFinite(offerId)) {
      setStoredGuestOfferAccess(offerId, tokenFromUrl);
      const next = new URLSearchParams(searchParams);
      next.delete('token');
      setSearchParams(next, { replace: true });
    }
  }, [tokenFromUrl, offerId, searchParams, setSearchParams]);

  const token = tokenFromUrl || getStoredGuestOfferToken();
  const storedOfferId = getStoredGuestOfferId();

  const probeClient = useMemo(
    () =>
      new ApolloClient({
        cache: new InMemoryCache(),
        link: new HttpLink({
          uri: httpLinkUrl,
          credentials: 'include',
        }),
      }),
    [],
  );

  const client = useMemo(() => {
    const authLink = setContext((_, { headers }) => ({
      headers: {
        ...headers,
        seminargo_offer_token: token || '',
        seminargo_offer_id: Number.isFinite(offerId) ? `${offerId}` : '',
      },
    }));
    return new ApolloClient({
      cache: new InMemoryCache(),
      link: from([
        authLink,
        new HttpLink({
          uri: httpLinkUrl,
          credentials: 'include',
        }),
      ]),
    });
  }, [token, offerId]);

  if (!Number.isFinite(offerId)) {
    return <Navigate to="/login" replace />;
  }

  if (!token) {
    return (
      <Box p={4}>
        <Typography color="error">Invalid or missing offer access link.</Typography>
        <Button
          component={Link}
          to={{ pathname: '/login', search: `?from=${encodeURIComponent(`/offers/${offerId}`)}` }}
          sx={{ mt: 2 }}
        >
          {i18next.t('offer-guest-sign-in')}
        </Button>
      </Box>
    );
  }

  if (storedOfferId != null && storedOfferId !== offerId) {
    return (
      <Box p={4}>
        <Typography color="error">This access link does not match the offer.</Typography>
      </Box>
    );
  }

  return (
    <ApolloProvider client={client}>
      <CssBaseline />
      <GuestOfferPageInner offerId={offerId} probeClient={probeClient} />
    </ApolloProvider>
  );
}
